/**
 * 9 case-conversion variants. All implemented as a small token-pipeline
 * to avoid the trap of pairwise-implementing every case combination
 * (which is how every pre-modern util library ended up with quirky
 * edge cases).
 *
 * Pipeline:
 *   `splitWords(input)` → array of lowercase tokens
 *   `joinAs(tokens, kind)` → single string
 *
 * Why a token pipeline:
 *   - Idempotency: kebab(camelCase("foo bar")) === "foo-bar".
 *   - Round-trip: switching from one case to another goes through the
 *     same canonical "list of words" shape, so all 9 cases share one
 *     source of truth for what counts as a word boundary.
 *   - Number / digit handling: "v1Beta" splits into ['v','1','beta']
 *     which downstream code can re-stitch by joining without a
 *     separator inside camel/pascal.
 *
 * CJK passes through verbatim; Chinese characters are not segmented.
 * The "case" itself doesn't apply to ideographic letters.
 */

export type CaseKind =
  | 'camel'
  | 'pascal'
  | 'kebab'
  | 'snake'
  | 'screamSnake'
  | 'dot'
  | 'path'
  | 'sentence'
  | 'title';

export const CASE_KINDS: ReadonlyArray<CaseKind> = [
  'camel',
  'pascal',
  'kebab',
  'snake',
  'screamSnake',
  'dot',
  'path',
  'sentence',
  'title',
];

export const CASE_LABEL: Record<CaseKind, string> = {
  camel: 'camelCase',
  pascal: 'PascalCase',
  kebab: 'kebab-case',
  snake: 'snake_case',
  screamSnake: 'SCREAMING_SNAKE',
  dot: 'dot.case',
  path: 'path/case',
  sentence: 'Sentence case',
  title: 'Title Case',
};

/* ------------------------------------------------------------------ */
/* Tokeniser                                                           */
/* ------------------------------------------------------------------ */

/**
 * Break `input` into a list of normalised lowercase tokens.
 *
 * Boundaries are determined by, in order:
 *   1. Any non-alphanumeric run (spaces, dashes, dots, slashes, ...).
 *   2. Lowercase-then-uppercase (`fooBar` → `foo|Bar`).
 *   3. Multi-uppercase-then-lower-or-acronym-end (`HTTPRequest` →
 *      `HTTP|Request`).
 *   4. Letter-to-digit and digit-to-letter (`v1Beta` → `v|1|Beta`).
 *
 * CJK / ideographic codepoints are kept inside the token they appear
 * in (we do not split inside `中文标识`).
 */
export function splitWords(input: string): string[] {
  if (!input) return [];

  /* Stage 1: insert a delimiter at every internal case boundary so
   * we can lower-case the whole thing and then split on a single
   * Unicode whitespace/punct class. */
  let s = input;
  /* Lower-then-upper: `fooBar` → `foo Bar` */
  s = s.replace(/([\p{Ll}\p{Lo}])(\p{Lu})/gu, '$1 $2');
  /* Acronym tail: `HTTPRequest` → `HTTP Request` */
  s = s.replace(/(\p{Lu})(\p{Lu}\p{Ll})/gu, '$1 $2');
  /* Letter ↔ digit boundaries */
  s = s.replace(/([\p{L}])(\d)/gu, '$1 $2');
  s = s.replace(/(\d)([\p{L}])/gu, '$1 $2');

  /* Stage 2: split on any run of non-alphanumeric chars. We use the
   * Unicode property `[^\p{L}\p{N}]+` so CJK letters survive. */
  const tokens = s.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  /* Stage 3: lowercase ASCII letters; CJK is unaffected by `toLowerCase`. */
  return tokens.map((t) => t.toLowerCase());
}

/* ------------------------------------------------------------------ */
/* Joiners                                                             */
/* ------------------------------------------------------------------ */

function ucFirst(s: string): string {
  if (!s) return s;
  /* `String.prototype.toUpperCase` works for the first ASCII letter;
   * CJK first char passes through unchanged. */
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function joinAs(tokens: string[], kind: CaseKind): string {
  if (tokens.length === 0) return '';
  switch (kind) {
    case 'camel': {
      return tokens
        .map((t, i) => (i === 0 ? t : ucFirst(t)))
        .join('');
    }
    case 'pascal': {
      return tokens.map(ucFirst).join('');
    }
    case 'kebab':
      return tokens.join('-');
    case 'snake':
      return tokens.join('_');
    case 'screamSnake':
      return tokens.map((t) => t.toUpperCase()).join('_');
    case 'dot':
      return tokens.join('.');
    case 'path':
      return tokens.join('/');
    case 'sentence': {
      const joined = tokens.join(' ');
      return ucFirst(joined);
    }
    case 'title':
      return tokens.map(ucFirst).join(' ');
    default: {
      const _e: never = kind;
      throw new Error(`unknown case: ${String(_e)}`);
    }
  }
}

export function toCase(input: string, kind: CaseKind): string {
  return joinAs(splitWords(input), kind);
}

/**
 * Convenience: produce all 9 variants in one pass.
 * Tokenises the input exactly once.
 */
export function toAllCases(input: string): Record<CaseKind, string> {
  const tokens = splitWords(input);
  const out = {} as Record<CaseKind, string>;
  for (const k of CASE_KINDS) {
    out[k] = joinAs(tokens, k);
  }
  return out;
}
